import { Examiner, ExaminersList, ExtendedExaminersList, FlatExtendedExaminersList } from './ExaminersStructure.types';
import { ExaminerFunction } from '../../pages/Assessment/Assessment.types';
import usePermission, { UserPermission } from '../../hooks/usePermission';

import { useCallback, useRef } from 'react';
import useSessionUser from '../../hooks/useSessionUser';
import { getCanUserSendMessageTo } from './helpers';
import { areObjectsEqual } from '../../utils/areObjectsEqual';

export const isCurrentUser = (userProfileSub?: string, examiner?: Examiner | null): boolean => {
  if (userProfileSub && examiner) {
    return userProfileSub === examiner.id;
  }
  return false;
};

export const useExaminersTree = (search: string) => {
  const allExtendedExaminersLists = useRef<FlatExtendedExaminersList>({});

  const {
    user: {
      profile: { sub: userProfileSub },
    },
    userAssessmentSessionInfo,
  } = useSessionUser();

  const hasPermissionAdminCommunicationAccess = usePermission(UserPermission.AdminCommunicationAccess);
  const hasPermissionAdminOkeCommunicationAccess = usePermission(UserPermission.AdminOkeCommunicationAccess);
  const hasPermissionExaminerCommunicationAccess = usePermission(UserPermission.ExaminerCommunicationAccess);
  const hasPermissionToCommunicationModule =
    hasPermissionAdminCommunicationAccess ||
    hasPermissionAdminOkeCommunicationAccess ||
    hasPermissionExaminerCommunicationAccess;

  const extendExaminersList = useCallback(
    (element: ExaminersList | ExtendedExaminersList): ExtendedExaminersList => {
      const isExaminerCurrentUser = isCurrentUser(userProfileSub, element.examiner);
      const displayName = element.examiner ? element.examiner.displayName : `(${ExaminerFunction[element.function]})`;
      const isDeskAssignedExaminer = element?.examiner;
      const canUserSendMessageTo = getCanUserSendMessageTo(
        element,
        userAssessmentSessionInfo,
        hasPermissionToCommunicationModule,
      );
      const blockadeTypeId = element.examiner?.examinerPermission?.isBlocked;
      const tooltipText = isDeskAssignedExaminer
        ? `${isDeskAssignedExaminer.firstName} ${isDeskAssignedExaminer.lastName}`
        : element.function.toString()
        ? `(${ExaminerFunction[element.function]})`
        : '';
      const newParams = {
        displayName,
        isCurrentUser: isExaminerCurrentUser,
        canUserSendMessageTo: !!canUserSendMessageTo,
        blockadeTypeId,
        tooltipText,
        ...(!!(search && element.inferiors.length) ? { isClosed: false } : {}),
      };

      const needsToBeRecreated = !areObjectsEqual(allExtendedExaminersLists.current[element.id], newParams);

      let inferiorsAreRecreated = false;
      const newInferiors = element.inferiors.map((el) => {
        const newData = extendExaminersList(el);
        if (newData !== el) {
          inferiorsAreRecreated = true;
        }
        return newData;
      });

      let extendedElement: ExtendedExaminersList | undefined;

      if (needsToBeRecreated || inferiorsAreRecreated) {
        extendedElement = {
          ...element,
          ...newParams,
        } as ExtendedExaminersList;
      } else {
        extendedElement = element as ExtendedExaminersList;

        Object.keys(newParams).forEach((key) => {
          (extendedElement as Record<string, unknown>)[key] = newParams[key as keyof typeof newParams];
        });
      }

      extendedElement.inferiors = newInferiors;
      allExtendedExaminersLists.current[element.id] = extendedElement;
      return extendedElement;
    },
    [hasPermissionToCommunicationModule, search, userAssessmentSessionInfo, userProfileSub],
  );

  return { extendExaminersList };
};
